import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Smartphone, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const STORE_LABEL = { app_store: "App Store", play_store: "Google Play" };
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

export const AppStoreBadge = ({ u }) => {
  if (u.payment_provider !== "revenuecat") return null;
  const active = u.subscription_status === "active";
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid={`user-app-sub-${u.email}`}>
      <Badge variant="outline" className={`text-[10px] gap-1 ${active ? "bg-primary/15 text-primary border-primary/30" : "text-muted-foreground"}`}>
        <Smartphone className="h-3 w-3" />{STORE_LABEL[u.rc_store] || "App"}{u.rc_sandbox ? " · test" : ""}
      </Badge>
      <span className="text-[10px] text-muted-foreground">{active ? (u.rc_will_renew === false ? "scade" : "rinnovo") : "scaduto"} {fmtDate(u.current_period_end)}</span>
      {active && u.rc_will_renew === false && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30" data-testid={`user-renew-off-${u.email}`}>Rinnovo disattivato</Badge>}
    </span>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-secondary/50 p-3" data-testid={testid}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-mono-data text-xl font-semibold">{value}</div>
  </div>
);

export const AppSubscribers = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/app-subscribers").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sync = async (u) => {
    setBusy(u.user_id);
    try {
      const { data: r } = await api.post(`/admin/users/${u.user_id}/revenuecat-sync`);
      toast.success(`${u.email}: ${r.is_premium ? "Premium attivo" : "non attivo"}`);
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return null;
  const s = data.stats;
  return (
    <Card className="rounded-2xl" data-testid="app-subscribers-card">
      <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> Abbonati da app (App Store / Google Play)</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Attivi totali" value={s.total_active} testid="app-stat-total" />
          <Stat label="App Store" value={s.app_store} testid="app-stat-app-store" />
          <Stat label="Google Play" value={s.play_store} testid="app-stat-play-store" />
          <Stat label="Mensili / Annuali" value={`${s.monthly} / ${s.yearly}`} testid="app-stat-plans" />
        </div>
        {(s.renew_off > 0 || s.billing_issues > 0 || s.sandbox > 0) && (
          <div className="flex gap-2 flex-wrap text-xs">
            {s.renew_off > 0 && <Badge variant="outline" className="text-destructive border-destructive/30">{s.renew_off} con rinnovo disattivato</Badge>}
            {s.billing_issues > 0 && <Badge variant="outline" className="text-amber-600 border-amber-400/40 gap-1"><AlertTriangle className="h-3 w-3" />{s.billing_issues} problemi di pagamento</Badge>}
            {s.sandbox > 0 && <Badge variant="outline">{s.sandbox} di test (sandbox)</Badge>}
          </div>
        )}
        {data.users.length === 0 ? <p className="text-sm text-muted-foreground text-center py-3">Nessun abbonato da app per ora.</p> : (
          <div className="space-y-2">
            {data.users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between gap-3 rounded-xl border p-3 flex-wrap" data-testid={`app-sub-row-${u.email}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{u.name || u.email} <span className="text-xs text-muted-foreground">{u.email}</span></div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex gap-2 flex-wrap items-center">
                    <span>{STORE_LABEL[u.store] || "—"}{u.sandbox ? " · test" : ""}</span>
                    <span>{u.plan === "yearly" ? "Annuale" : u.plan === "monthly" ? "Mensile" : "—"}</span>
                    <span>{u.status === "active" ? (u.will_renew ? "Rinnovo il" : "Scade il") : "Scaduto"} {fmtDate(u.current_period_end)}</span>
                    {u.status === "active" && !u.will_renew && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Rinnovo disattivato</Badge>}
                    {u.billing_issue && <Badge variant="outline" className="text-[10px] text-amber-600">Problema pagamento</Badge>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => sync(u)} disabled={busy === u.user_id} data-testid={`app-sub-sync-${u.email}`}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${busy === u.user_id ? "animate-spin" : ""}`} /> Aggiorna
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
